import React, { useEffect, useState, useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import { IStore } from "redux/store";
import { Dispatch } from "redux";
import toolActions from "redux/app/tool/actions";
import Select from "react-select";
import classNames from "classnames";
import clsx from "clsx";
import toast, { Toaster } from "react-hot-toast";
import { Alert, Intent, Icon } from "@blueprintjs/core";
import {
	clearIndicatorStyles,
	controlStyles,
	dropdownIndicatorStyles,
	menuStyles,
	optionStyles,
	placeholderStyles,
} from "redux/app/feedback/utils";
import { getTenantsOptionList } from "redux/app/tool/utils";
import { IOption } from "redux/app/feedback/@types";
import { IVessel } from "redux/app/feedback/@types";
import DateRangePickerVerbose from "components/_common/DateRangePickerVerbose";
import { Range, RangeKeyDict } from "react-date-range";
import InfiniteProgress from "components/_common/InfiniteProgress";
import { saveAs } from "file-saver";
import { logsPdfDefaultStaticRanges } from "redux/app/insight/utils";
import toolSelectors from "redux/app/tool/selectors";
import { defineds } from "redux/_common/utils";
import commonUtils from "redux/_common/utils";
import JobsHistoryButton from "./JobsHistoryButton";

interface IVesselStore {
	byIds: { [key: string]: IVessel };
	ids: string[];
}

type OwnProps = {
	onShowJobsHistory?: () => void;
};

type LogsPdfManagerProps = PropsFromRedux & OwnProps;

const LogsPdfManager = (props: LogsPdfManagerProps) => {
	const [isLoading, setIsLoading] = useState(false);
	const [selectedVesselOption, setSelectedVesselOption] =
		useState<IOption | null>(null);
	const [selectedLog, setSelectedLog] = useState<IOption | null>(null);
	const dateRange = props.dateRange;
	const [error, setError] = useState<string | null>(null);
	const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
	const [isPdfBookLoading, setPdfBookLoading] = useState<boolean>(false);
	const [showCoverPageAlert, setShowCoverPageAlert] = useState<boolean>(false);
	const [isZipLoading, setIsZipLoading] = useState<boolean>(false);

	useEffect(() => {
		props.loadTenants();
	}, []);

	useEffect(() => {
		return () => {
			// Cleanup function to reset state when component unmounts
			props.setTenant(undefined);
			props.clearLogsPdf();
			setSelectedVesselOption(null);
			setSelectedLog(null);
			setIsDatePickerOpen(false);
			setError(null);
			props.setDateRange([
				{
					startDate: defineds.past30days,
					endDate: defineds.endOfToday,
					key: "selection",
				},
			]);
		};
	}, []);

	useEffect(() => {
		setSelectedVesselOption(null);
		setSelectedLog(null);
		setIsDatePickerOpen(false);
		setError(null);
		props.setDateRange([
			{
				startDate: defineds.past30days,
				endDate: defineds.endOfToday,
				key: "selection",
			},
		]);
	}, [props.selectedTenantOption]);

	

	const loadData = async () => {
		if (
			!props.selectedTenantOption ||
			!selectedVesselOption ||
			!selectedLog ||
			!dateRange[0].startDate ||
			!dateRange[0].endDate
		) {
			setError("Please select tenant, vessel and log type.");
			toast.error("Please select tenant, vessel and log type.");
			return;
		}

		try {
			setError(null);
			// Clear previous logs before loading new ones
			props.clearLogsPdf();

			const tenantName = props.selectedTenantOption?.label;
			const vesselName = selectedVesselOption?.label;
			const startDate = commonUtils.correctToLocalDateISO(dateRange[0].startDate as Date);
			const endDate = commonUtils.correctToLocalDateISO(dateRange[0].endDate as Date, true);

			await props.loadLogsPdf(
				tenantName || "",
				vesselName || "",
				startDate,
				endDate,
				selectedLog?.value || ""
			);
		} catch (err) {
			setError("Failed to load logs. Please try again.");
			toast.error("Failed to load logs");
		} finally {
		}
	};

	useEffect(() => {
		// Load vessels when tenant changes
		if (props.selectedTenantOption?.value) {
			props.loadVessels(props.selectedTenantOption.value);
		}
	}, [props.selectedTenantOption]);

	const tenantOptions = useMemo(
		() => getTenantsOptionList(props.tenants),
		[props.tenants.ids]
	);

	const vesselOptions = useMemo(() => {
		return props.vessels.ids.map((id: string) => ({
			value: props.vessels.byIds[id].id,
			label: props.vessels.byIds[id].vesselName,
		}));
	}, [props.vessels.ids, props.vessels.byIds]);

	const logOptions = [
		{
			label: "Engine Log",
			value: "engineLogConfig",
		},
		{
			label: "Deck Log",
			value: "deckLogConfig",
		},
		{
			label: "Port Log",
			value: "portLogConfig",
		},
		{
			label: "GMDSS Radio Log",
			value: "gmdssRadioConfig",
		},
		{
			label: "Radar Log",
			value: "radarLogConfig",
		},
		{
			label: "Compass Log",
			value: "compassLogConfig",
		},
		{
			label: "Bridge Order Book",
			value: "bridgeOrderConfig",
		},
		{
			label: "Engine Order Book",
			value: "engineOrderConfig",
		},
		{
			label: "Medical Log",
			value: "medicalLogConfig",
		},
	];

	const handleDateRage = (range: RangeKeyDict) => {
		const { selection } = range;
		props.setDateRange([selection]);
	};

	const handleZipDownload = async () => {
		setIsZipLoading(true);

		const downloadPromise = async () => {
			const startDate = commonUtils.correctToLocalDateISO(dateRange[0].startDate as Date);
			const endDate = commonUtils.correctToLocalDateISO(dateRange[0].endDate as Date, true);

			const response = await fetch(
				`${commonUtils.getLogsPdfBaseUrl()}/logs-pdf-zip?tenantName=${
					props.selectedTenantOption?.label
				}&vesselName=${selectedVesselOption?.label}&code=${
					selectedLog?.value
				}&startDate=${startDate}&endDate=${endDate}`
			);
			if (!response.ok) {
				throw new Error("Failed to download zip");
			}

			//  getting the response as a zip file and triggering download
			const zipBlob = await response.blob();
			const tenantName = props.selectedTenantOption?.label || "tenant";
			const vesselName = selectedVesselOption?.label || "vessel";
			const logType = selectedLog?.label || "log";
			const date = `${startDate.split("T")[0]}_${endDate.split("T")[0]}`;

			const filename = `${tenantName}_${vesselName}_${logType}_logs_${date}.zip`;
			saveAs(zipBlob, filename);
		};

		try {
			await toast.promise(downloadPromise(), {
				loading: "Generating ZIP...",
				success: (filename) => `ZIP downloaded successfully!`,
				error: "Failed to download ZIP",
			});
		} catch (error) {
			console.error("Error downloading ZIP:", error);
		} finally {
			setIsZipLoading(false);
		}
	};

	const EPSCODES = [
		{ id: "portLogConfig", title: "Port Log", type: "LOG" },
		{ id: "compassLogConfig", title: "Compass Error Log", type: "LOG" },
		{ id: "deckLogConfig", title: "Deck Log", type: "LOG" },
		{ id: "engineLogConfig", title: "Engine Log", type: "LOG" },
		{ id: "gmdssRadioConfig", title: "GMDSS Radio Log", type: "LOG" },
	];
	const TEEKAYCODES = [
		{ id: "compassLogConfig", title: "Compass Error Log", type: "LOG" },
		{ id: "engineLogConfig", title: "Engine Log", type: "LOG" },
		{ id: "gmdssRadioConfig", title: "GMDSS Radio Log", type: "LOG" },
	];
	const hasCoverPages = {
		eastern_pacific_shipping: EPSCODES,
		teekay: TEEKAYCODES,
	};

	const handleDownloadBookWithCoverPage = async (includeCoverPage: boolean) => {
		const downloadPromise = async () => {
			const startDate = commonUtils.correctToLocalDateISO(dateRange[0].startDate as Date);
			const endDate = commonUtils.correctToLocalDateISO(dateRange[0].endDate as Date, true);

			const response = await fetch(
				`${commonUtils.getLogsPdfBaseUrl()}/logs-pdf-book?tenantName=${
					props.selectedTenantOption?.label
				}&vesselId=${selectedVesselOption?.value}&code=${
					selectedLog?.value
				}&startDate=${startDate}&endDate=${endDate}&include_coverpage=${includeCoverPage}`
			);
			if (!response.ok) {
				throw new Error("Failed to download book");
			}

			// Convert response to blob and trigger download
			const pdfBlob = await response.blob();
			const tenantName = props.selectedTenantOption?.label || "tenant";
			const vesselName = selectedVesselOption?.label || "vessel";
			const logType = selectedLog?.label || "log";
			const dateStr = `${startDate.split("T")[0]}_${endDate.split("T")[0]}`;

			const filename = `${logType}_book_${tenantName}_${vesselName}_${dateStr}${
				includeCoverPage ? "_coverpage" : ""
			}.pdf`;
			saveAs(pdfBlob, filename);

			return filename;
		};

		try {
			await toast.promise(downloadPromise(), {
				loading: "Generating PDF book...",
				success: (filename) => `Book downloaded successfully!`,
				error: "Failed to download book",
			});
		} catch (error) {
			console.error("Error downloading book:", error);
		} finally {
			setPdfBookLoading(false);
		}
	};

	const handleDownloadBook = () => {
		const tenantName =
			props.selectedTenantOption?.label?.split(" ").join("_").toLowerCase() ||
			"";
		const logType = selectedLog?.value || "";

		console.log({
			tenantName,
			logType,
			selectedLog,
			tenant: props.selectedTenantOption,
		});

		if (
			tenantName in hasCoverPages &&
			hasCoverPages[tenantName as keyof typeof hasCoverPages].some(
				(code) => code.id === logType
			)
		) {
			setShowCoverPageAlert(true);
			return;
		} else {
			setPdfBookLoading(true);
			handleDownloadBookWithCoverPage(false);
		}
	};

	const handleCoverPageConfirm = async (includeCoverPage: boolean) => {
		setShowCoverPageAlert(false);
		setPdfBookLoading(true);

		await handleDownloadBookWithCoverPage(includeCoverPage);
	};

	const handleCoverPageCancel = () => {
		setShowCoverPageAlert(false);
	};

	return (
		<div className="bg-white h-[calc(100vh-98px)] m-2 rounded-md overflow-y-auto p-6">
			<Toaster
				position="top-right"
				toastOptions={{
					duration: 3000,
					style: {
						background: "#333",
						color: "#fff",
					},
				}}
			/>

			<div className="flex justify-between items-center mb-8">
				<div className="flex items-center gap-4">
					<button
						onClick={() => window.history.back()}
						className="text-gray-600 hover:text-gray-900 transition-colors"
					>
						<svg
							className="w-6 h-6"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M10 19l-7-7m0 0l7-7m-7 7h18"
							/>
						</svg>
					</button>
					<h2 className="text-2xl font-semibold">Logs PDF</h2>
				</div>
				<div className="flex gap-2 items-center">
					<div className="flex items-center gap-2">
						{props.onShowJobsHistory && (
							<JobsHistoryButton onClick={props.onShowJobsHistory} />
						)}
					</div>
				</div>
			</div>

			<div className="bg-gray-50 p-6 rounded-lg mb-6 border border-gray-200">
				<h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
				<div className="flex flex-wrap gap-4">
					<div className="w-64">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Tenant
						</label>
						<Select
							id="tenants"
							isClearable
							isSearchable
							isDisabled={props.jobToLoadPdfLogs?.isLoading || isLoading}
							key={"tenants" + props.selectedTenantOption?.value}
							placeholder={"Search tenants"}
							unstyled
							value={props.selectedTenantOption}
							options={tenantOptions}
							onChange={(newValue) => {
								props.setTenant(newValue ?? undefined);
							}}
							className={classNames("text-sm")}
							menuPosition="fixed"
							classNames={{
								option: ({ isFocused, isSelected }) =>
									clsx(
										isFocused && optionStyles.focus,
										isSelected && optionStyles.selected,
										optionStyles.base
									),
								dropdownIndicator: () => dropdownIndicatorStyles,
								menu: () => menuStyles,
								clearIndicator: () => clearIndicatorStyles,
								placeholder: () => placeholderStyles,
								control: ({ isFocused, isDisabled }) =>
									clsx(
										isFocused ? controlStyles.focus : controlStyles.nonFocus,
										isDisabled ? controlStyles.disabled : controlStyles.base
									),
								valueContainer: ({ isDisabled }) =>
									clsx("pl-1", isDisabled && "bg-gray-200 ml-2"),
							}}
						/>
					</div>

					<div className="w-64">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Vessel
						</label>
						<Select
							id="vessels"
							isClearable
							isSearchable
							isDisabled={
								isLoading ||
								props.jobToLoadPdfLogs?.isLoading ||
								!props.selectedTenantOption
							}
							placeholder={"Search vessels"}
							unstyled
							value={selectedVesselOption}
							options={vesselOptions}
							onChange={(newValue) => {
								setSelectedVesselOption(newValue);
							}}
							className={classNames("text-sm")}
							menuPosition="fixed"
							classNames={{
								option: ({ isFocused, isSelected }) =>
									clsx(
										isFocused && optionStyles.focus,
										isSelected && optionStyles.selected,
										optionStyles.base
									),
								dropdownIndicator: () => dropdownIndicatorStyles,
								menu: () => menuStyles,
								clearIndicator: () => clearIndicatorStyles,
								placeholder: () => placeholderStyles,
								control: ({ isFocused, isDisabled }) =>
									clsx(
										isFocused ? controlStyles.focus : controlStyles.nonFocus,
										isDisabled ? controlStyles.disabled : controlStyles.base
									),
								valueContainer: ({ isDisabled }) =>
									clsx("pl-1", isDisabled && "bg-gray-200 ml-2"),
							}}
						/>
					</div>
					<div className="w-64">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Log
						</label>
						<Select
							id="log"
							isClearable
							isSearchable
							isDisabled={
								isLoading ||
								props.jobToLoadPdfLogs?.isLoading ||
								!props.selectedTenantOption ||
								!selectedVesselOption
							}
							placeholder={"Select log"}
							unstyled
							value={selectedLog}
							options={logOptions}
							onChange={(newValue) => {
								setSelectedLog(newValue);
							}}
							className={classNames("text-sm")}
							menuPosition="fixed"
							classNames={{
								option: ({ isFocused, isSelected }) =>
									clsx(
										isFocused && optionStyles.focus,
										isSelected && optionStyles.selected,
										optionStyles.base
									),
								dropdownIndicator: () => dropdownIndicatorStyles,
								menu: () => menuStyles,
								clearIndicator: () => clearIndicatorStyles,
								placeholder: () => placeholderStyles,
								control: ({ isFocused, isDisabled }) =>
									clsx(
										isFocused ? controlStyles.focus : controlStyles.nonFocus,
										isDisabled ? controlStyles.disabled : controlStyles.base
									),
								valueContainer: ({ isDisabled }) =>
									clsx("pl-1", isDisabled && "bg-gray-200 ml-2"),
							}}
						/>
					</div>
					<div className="w-64 mt-6">
						<DateRangePickerVerbose
							handleOnChange={handleDateRage}
							onClose={() => setIsDatePickerOpen(false)}
							handleOpen={() => setIsDatePickerOpen(true)}
							isOpen={isDatePickerOpen}
							dateRange={dateRange}
							staticRanges={logsPdfDefaultStaticRanges}
							disabled={
								isLoading || props.jobToLoadPdfLogs?.isLoading || !selectedLog
							}
						/>
					</div>
					<button
						className="self-end py-1 mb-1 inline-flex items-center gap-2 bg-blue-500 text-white px-4 rounded hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
						onClick={loadData}
						disabled={
							props.jobToLoadPdfLogs?.isLoading ||
							isLoading ||
							isZipLoading ||
							isPdfBookLoading ||
							!props.selectedTenantOption ||
							!selectedVesselOption ||
							!selectedLog ||
							!dateRange[0].startDate ||
							!dateRange[0].endDate
						}
					>
						{props.jobToLoadPdfLogs?.isLoading ? "Loading..." : "Load Logs PDF"}
					</button>
				</div>
			</div>

			{error && (
				<div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
					<div className="flex items-center">
						<svg
							className="w-5 h-5 text-red-400 mr-2"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
						<span className="text-sm text-red-700">{error}</span>
					</div>
				</div>
			)}

			<div className="mb-6 overflow-x-auto flex flex-col gap-4">
				<div className="flex justify-end gap-4 ">
					<button
						className="py-2 px-4 rounded-sm  hover:bg-gray-100 disabled:text-gray-200 border shadow transition-colors flex items-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed text-black"
						onClick={handleZipDownload}
						disabled={
							isLoading ||
							isZipLoading ||
							props.jobToLoadPdfLogs?.isLoading ||
							!props.logspdf ||
							!props.logspdf.data ||
							!props.logspdf.data.data ||
							props.logspdf.data.data.length === 0
						}
					>
						{isZipLoading ? (
							<div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
						) : (
							<svg
								xmlns="http://www.w3.org/2000/svg"
								className="h-5 w-5"
								viewBox="0 0 20 20"
								fill="currentColor"
							>
								<path
									fillRule="evenodd"
									d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
									clipRule="evenodd"
								/>
							</svg>
						)}
						{isZipLoading ? "Downloading..." : "Download All (ZIP)"}
					</button>
					<button
						className="py-2 px-4 rounded-sm  hover:bg-gray-100 disabled:text-gray-200 border shadow transition-colors flex items-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed text-black"
						onClick={handleDownloadBook}
						disabled={
							isLoading ||
							isPdfBookLoading ||
							props.jobToLoadPdfLogs?.isLoading ||
							!props.logspdf ||
							!props.logspdf.data ||
							!props.logspdf.data.data ||
							props.logspdf.data.data.length === 0
						}
					>
						{isPdfBookLoading ? (
							<div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
						) : (
							<svg
								xmlns="http://www.w3.org/2000/svg"
								className="h-5 w-5"
								viewBox="0 0 20 20"
								fill="currentColor"
							>
								<path
									fillRule="evenodd"
									d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
									clipRule="evenodd"
								/>
							</svg>
						)}
						{isPdfBookLoading ? "Downloading..." : "Download Book (PDF)"}
					</button>
				</div>
				<div>
					<InfiniteProgress
						isLoading={props.jobToLoadPdfLogs?.isLoading}
						isSpacedOut={false}
					/>
					<table
						className={classNames(
							"w-full relative mb-6  border border-gray-300 rounded-sm bp3-html-table bp3-interactive",
							{ "animate-pulse": props.jobToLoadPdfLogs?.isLoading }
						)}
					>
						<thead className="relative">
							<tr>
								<th className="text-sm text-gray-500">No.</th>
								<th className="text-sm text-gray-500">Vessel Name</th>
								<th className="text-sm text-gray-500">Log type</th>
								<th className="text-sm text-center text-gray-500">Date</th>
								<th className="text-sm text-center text-gray-500">Actions</th>
							</tr>
						</thead>
						<tbody
							className={classNames({
								"h-0 opacity-0 transition-all duration-100 max-h-0":
									props.jobToLoadPdfLogs?.isLoading,
								"h-auto opacity-100 transition-all duration-1000 delay-300 max-h-auto":
									!props.jobToLoadPdfLogs?.isLoading,
							})}
						>
							{!props.jobToLoadPdfLogs?.isLoading && (
								<>
									{!props?.logspdf?.data?.data ||
									props.logspdf.data.data.length === 0 ? (
										<EmptyState
											hasFiltersApplied={
												!!(
													props.selectedTenantOption &&
													selectedVesselOption &&
													selectedLog &&
													dateRange[0].startDate &&
													dateRange[0].endDate
												)
											}
											onClearFilters={() => {
												props.setTenant(undefined);
												setSelectedVesselOption(null);
												setSelectedLog(null);
												props.setDateRange([
													{
														startDate: defineds.past30days,
														endDate: defineds.endOfToday,
														key: "selection",
													},
												]);
											}}
										/>
									) : (
										props.logspdf.data.data.map(
											(
												logPdf: {
													key: string;
													url: string;
												},
												index: number
											) => (
												<tr
													key={index}
													className="hover:bg-gray-50 transition-colors duration-150 w-full"
												>
													<td>
														<div className="text-sm font-medium text-gray-500">
															{index + 1}
														</div>
													</td>
													<td>
														<div className="text-sm font-medium text-gray-900">
															{logPdf.key.split("/").at(1)}
														</div>
													</td>
													<td>
														<div className="text-sm font-medium text-gray-900">
															{logOptions.find(
																(option: { label: string; value: string }) =>
																	option.value.toLocaleLowerCase() ===
																	logPdf.key
																		.split("/")
																		.at(2)
																		?.toLocaleLowerCase()
															)?.label || "Unknown Log Type"}
														</div>
													</td>
													<td className="text-center">
														<div className="text-sm font-medium text-gray-900">
															{logPdf.key
																.split("/")
																.at(-1)
																?.replace(".pdf", "")}
														</div>
													</td>
													<td className="text-center">
														<div className="text-sm font-medium text-gray-900">
															<span className="text-blue-500">
																<a
																	href={logPdf.url}
																	target="_blank"
																	rel="noopener noreferrer"
																	className="hover:underline"
																>
																	View PDF
																</a>
															</span>
														</div>
													</td>
												</tr>
											)
										)
									)}
								</>
							)}
						</tbody>
					</table>
				</div>
			</div>

			<Alert
				isOpen={showCoverPageAlert}
				onClose={handleCoverPageCancel}
				onCancel={() => handleCoverPageConfirm(true)}
				onConfirm={() => handleCoverPageConfirm(false)}
				intent={Intent.PRIMARY}
				icon={null}
				cancelButtonText="Yes"
				confirmButtonText="No"
			>
				<div className="flex items-center justify-between mb-3">
					<div className="flex items-center">
						<Icon icon="document" size={18} className="text-blue-600" />
						<span className="font-semibold text-lg text-gray-900">
							Cover Page Options
						</span>
					</div>
					<button
						onClick={handleCoverPageCancel}
						className="text-gray-400 hover:text-gray-600 focus:outline-none"
						aria-label="Close"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							className="h-5 w-5"
							viewBox="0 0 20 20"
							fill="currentColor"
						>
							<path
								fillRule="evenodd"
								d="M10 8.586l4.95-4.95a1 1 0 011.414 1.414L11.414 10l4.95 4.95a1 1 0 01-1.414 1.414L10 11.414l-4.95 4.95a1 1 0 01-1.414-1.414L8.586 10l-4.95-4.95A1 1 0 015.05 3.636L10 8.586z"
								clipRule="evenodd"
							/>
						</svg>
					</button>
				</div>
				<div className="mb-3 text-sm text-gray-700">
					Do you want to print the selected log with the cover pages?
				</div>
			</Alert>
		</div>
	);
};

interface EmptyStateProps {
	hasFiltersApplied: boolean;
	onClearFilters: () => void;
}

const EmptyState = ({ hasFiltersApplied, onClearFilters }: EmptyStateProps) => {
	if (!hasFiltersApplied) {
		// No filters applied - show initial state
		return (
			<tr>
				<td colSpan={5} className="text-center py-12">
					<div className="flex flex-col items-center justify-center text-gray-500">
						<svg
							className="w-16 h-16 mb-4 text-gray-300"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={1.5}
								d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
							/>
						</svg>
						<h3 className="text-xl font-medium text-gray-900 mb-2">
							Generate PDF Logs
						</h3>
						<p className="text-sm text-gray-500 mb-4 max-w-md text-center">
							Select a tenant, vessel, log type, and date range above to load
							and view your PDF logs.
						</p>
					</div>
				</td>
			</tr>
		);
	}

	// Filters applied but no results
	return (
		<tr>
			<td colSpan={5} className="text-center py-12">
				<div className="flex flex-col items-center justify-center text-gray-500">
					<svg
						className="w-16 h-16 mb-4 text-gray-300"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={1.5}
							d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
						/>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={1.5}
							d="M15 15l5-5m0 0l-5-5m5 5H9"
						/>
					</svg>
					<h3 className="text-xl font-medium text-gray-900 mb-2">
						No PDF Logs Found
					</h3>
					<p className="text-sm text-gray-500 mb-6 max-w-md text-center">
						No PDF logs were found for the selected criteria. Try adjusting your
						filters or selecting a different date range.
					</p>
				</div>
			</td>
		</tr>
	);
};

function mapStateToProps(store: IStore) {
	const toolStore = (store as any).app.tool;
	const authStore = store.app.auth;
	const isAdmin = authStore.user?.roleCategory === "admin";
	const dateRange = toolSelectors.getDateRange(toolStore);

	const isSpecificAdmin =
		isAdmin &&
		authStore.user?.email?.toLowerCase() === "abdul.ghani@volteo.com";

	return {
		tenants: toolStore?.tenants || { byIds: {}, ids: [] },
		vessels: (toolStore?.vessels || { byIds: {}, ids: [] }) as IVesselStore,
		selectedTenantOption: toolStore?.selectedTenantOption,
		isAdmin,
		isSpecificAdmin,
		jobToLoadPdfLogs: toolSelectors.jobToLoadLogsPdf(toolStore),
		logspdf: toolStore?.logsPdf || { data: { data: [] } },
		dateRange,
	};
}

function mapDispatchToProps(dispatch: Dispatch) {
	return {
		loadTenants() {
			dispatch(toolActions.commands.toolTenantListLoad());
		},
		loadVessels(tenantId: string) {
			dispatch(toolActions.commands.toolTenantSpecificVesselLoad(tenantId));
		},
		loadLogsPdf(
			tenantId: string,
			vesselId: string,
			startDate: string,
			endDate: string,
			code: string
		) {
			return new Promise((resolve, reject) => {
				dispatch(
					toolActions.commands.toolLogsPdfListLoad(
						tenantId,
						vesselId,
						startDate,
						endDate,
						code
					)
				);
				resolve(true);
			});
		},
		clearLogsPdf() {
			dispatch(toolActions.document.toolLogsPdfListSet([]));
		},
		setTenant(tenantOption?: IOption) {
			dispatch(
				toolActions.document.toolReleaseTenantOptionSelect(tenantOption)
			);
		},
		setDateRange(dateRange: Range[]) {
			dispatch(toolActions.document.toolLogsPdfDateRangeSet(dateRange));
		},
	};
}

const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(LogsPdfManager);
